import Reflux from "reflux";
import * as Immutable from "immutable";
import SharedEntity from "logic/permissions/SharedEntity";
import fetch from "logic/rest/FetchProvider";
import { qualifyUrl } from "util/URLUtils";
import { singletonStore } from "logic/singleton";
import Role from "logic/roles/Role";
import UserOverview from "logic/users/UserOverview";
import PaginationURL from "util/PaginationURL";
import TeamsActions from "../actions/TeamsActions";
import Team from "../logic/Team";
const apiPrefix = "/plugins/org.graylog.plugins.security";
const ApiRoutes = {
  TeamsApiController: {
    loadTeamsForUser: (userId) => ({ url: `${apiPrefix}/teams/member/${userId}` }),
    loadTeamsForRole: (roleId) => ({ url: `${apiPrefix}/authz/roles/${roleId}/teams` }),
    loadUsersForTeam: (teamId) => ({ url: `${apiPrefix}/teams/${teamId}/members` }),
    loadRolesForTeam: (teamId) => ({ url: `${apiPrefix}/teams/${teamId}/roles` }),
    loadPaginated: () => ({ url: `${apiPrefix}/teams` }),
    addMembers: (teamId) => ({ url: `${apiPrefix}/teams/${teamId}/members` }),
    addMemberToTeams: (userId) => ({ url: `${apiPrefix}/teams/${userId}/teams` }),
    addRoleToTeams: (roleId) => ({ url: `${apiPrefix}/authz/roles/${roleId}/teams/add` }),
    removeMember: (teamId, userId) => ({ url: `${apiPrefix}/teams/${teamId}/members/${userId}` }),
    removeRole: (roleId) => ({ url: `${apiPrefix}/authz/roles/${roleId}/teams/remove` }),
    create: () => ({ url: `${apiPrefix}/teams` }),
    delete: (teamId) => ({ url: `${apiPrefix}/teams/${teamId}` }),
    load: (teamId) => ({ url: `${apiPrefix}/teams/${teamId}` }),
    update: (teamId) => ({ url: `${apiPrefix}/teams/${teamId}` }),
    teamSharesPaginated: (teamId) => ({ url: `${apiPrefix}/teams/shares/${teamId}` })
  }
};
const formatPaginatedSharesResponse = ({
  additional_queries: additionalQueries,
  context,
  count,
  entities,
  page,
  per_page: perPage,
  query,
  total
}) => ({
  list: Immutable.List(entities.map((se) => SharedEntity.fromJSON(se))),
  context: {
    granteeCapabilities: context.grantee_capabilities
  },
  pagination: {
    additionalQueries,
    page,
    perPage,
    query,
    count,
    total
  }
});
const _responseToPaginatedList = ({ count, total, page, per_page, query, teams, context }) => ({
  context: {
    users: Immutable.Map(context == null ? void 0 : context.users),
    roles: Immutable.Map(context == null ? void 0 : context.roles)
  },
  list: Immutable.List(teams.map(Team.fromJSON)),
  pagination: {
    page,
    perPage: per_page,
    query,
    count,
    total
  }
});
const _responseToPaginatedUserList = ({
  count,
  total,
  page,
  per_page,
  query,
  users,
  context
}) => ({
  list: Immutable.List(users.map((u) => UserOverview.fromJSON(u))),
  pagination: {
    page,
    perPage: per_page,
    query,
    count,
    total
  },
  adminUser: (context == null ? void 0 : context.admin_user) ? UserOverview.fromJSON(context == null ? void 0 : context.admin_user) : void 0
});
const _responseToPaginatedRolesList = ({
  count,
  total,
  page,
  per_page,
  query,
  roles,
  context
}) => ({
  list: Immutable.List(roles.map((r) => Role.fromJSON(r))),
  pagination: {
    page,
    perPage: per_page,
    query,
    count,
    total
  },
  context
});
const TeamsStore = singletonStore(
  "enterprise.security.TeamsStore",
  () => Reflux.createStore({
    listenables: [TeamsActions],
    create(team, share_request) {
      const { url } = ApiRoutes.TeamsApiController.create();
      const promise = fetch("POST", qualifyUrl(url), { entity: team.toJSON(), share_request }).then(Team.fromJSON);
      TeamsActions.create.promise(promise);
      return promise;
    },
    load(teamId) {
      const { url } = ApiRoutes.TeamsApiController.load(teamId);
      const promise = fetch("GET", qualifyUrl(url)).then(Team.fromJSON);
      TeamsActions.load.promise(promise);
      return promise;
    },
    update(team) {
      const { url } = ApiRoutes.TeamsApiController.update(team.id);
      const promise = fetch("PUT", qualifyUrl(url), team.toJSON()).then(Team.fromJSON);
      TeamsActions.update.promise(promise);
      return promise;
    },
    delete(team) {
      const { url } = ApiRoutes.TeamsApiController.delete(team.id);
      const promise = fetch("DELETE", qualifyUrl(url));
      TeamsActions.delete.promise(promise);
      return promise;
    },
    addMembers(teamId, userIds) {
      const { url } = ApiRoutes.TeamsApiController.addMembers(teamId);
      const promise = fetch("PUT", qualifyUrl(url), userIds.toArray()).then(Team.fromJSON);
      TeamsActions.addMembers.promise(promise);
      return promise;
    },
    addMemberToTeams(userId, teamIds) {
      const { url } = ApiRoutes.TeamsApiController.addMemberToTeams(userId);
      const promise = fetch("PUT", qualifyUrl(url), teamIds.toArray());
      TeamsActions.addMemberToTeams.promise(promise);
      return promise;
    },
    addRoleToTeams(roleId, teamIds) {
      const { url } = ApiRoutes.TeamsApiController.addRoleToTeams(roleId);
      const promise = fetch("PUT", qualifyUrl(url), teamIds.toArray());
      TeamsActions.addRoleToTeams.promise(promise);
      return promise;
    },
    removeMember(teamId, userId) {
      const { url } = ApiRoutes.TeamsApiController.removeMember(teamId, userId);
      const promise = fetch("DELETE", qualifyUrl(url)).then(Team.fromJSON);
      TeamsActions.removeMember.promise(promise);
      return promise;
    },
    removeRole(roleId, teamId) {
      const { url } = ApiRoutes.TeamsApiController.removeRole(roleId);
      const promise = fetch("PUT", qualifyUrl(url), teamId);
      TeamsActions.removeRole.promise(promise);
      return promise;
    },
    loadRolesForTeam(teamId, _teamName, { page, perPage, query }) {
      const url = PaginationURL(ApiRoutes.TeamsApiController.loadRolesForTeam(teamId).url, page, perPage, query);
      const promise = fetch("GET", qualifyUrl(url)).then(_responseToPaginatedRolesList);
      TeamsActions.loadRolesForTeam.promise(promise);
      return promise;
    },
    loadUsersForTeam(teamId, _teamName, { page, perPage, query }) {
      const url = PaginationURL(ApiRoutes.TeamsApiController.loadUsersForTeam(teamId).url, page, perPage, query);
      const promise = fetch("GET", qualifyUrl(url)).then(_responseToPaginatedUserList);
      TeamsActions.loadUsersForTeam.promise(promise);
      return promise;
    },
    loadTeamsForUser(userId, { page, perPage, query }) {
      const url = PaginationURL(ApiRoutes.TeamsApiController.loadTeamsForUser(userId).url, page, perPage, query);
      const promise = fetch("GET", qualifyUrl(url)).then(_responseToPaginatedList);
      TeamsActions.loadTeamsForUser.promise(promise);
      return promise;
    },
    loadTeamsForRole(roleId, { page, perPage, query }) {
      const url = PaginationURL(ApiRoutes.TeamsApiController.loadTeamsForRole(roleId).url, page, perPage, query);
      const promise = fetch("GET", qualifyUrl(url)).then(_responseToPaginatedList);
      TeamsActions.loadTeamsForRole.promise(promise);
      return promise;
    },
    loadTeamsPaginated({ page, perPage, query }) {
      const url = PaginationURL(ApiRoutes.TeamsApiController.loadPaginated().url, page, perPage, query);
      const promise = fetch("GET", qualifyUrl(url)).then(_responseToPaginatedList);
      TeamsActions.loadTeamsPaginated.promise(promise);
      return promise;
    },
    loadTeamSharesPaginated(teamId, { page, perPage, query, additionalQueries }) {
      const url = PaginationURL(
        ApiRoutes.TeamsApiController.teamSharesPaginated(teamId).url,
        page,
        perPage,
        query,
        additionalQueries
      );
      const promise = fetch("GET", qualifyUrl(url)).then(formatPaginatedSharesResponse);
      TeamsActions.loadTeamSharesPaginated.promise(promise);
      return promise;
    }
  })
);
export { TeamsActions, TeamsStore };
